// src/components/SurveyModal.js
import React, { useMemo, useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { generatePdfReport, downloadPdfReport } from './PdfReportGenerator';

// Вспомогательные функции для расчета статистики (вынесены в начало)
const calculateAverageRating = (data) => {
  if (!data) return '0.0';
  
  const breakfastRatings = data.filter(d => 
    d.question_code === 'Q10' && d.answer?.includes('балл')
  );
  
  let totalScore = 0;
  let totalWeight = 0;
  
  breakfastRatings.forEach(rating => {
    const score = parseInt(rating.answer);
    const weight = parseFloat(rating.percentage.toString().replace(',', '.'));
    totalScore += score * weight;
    totalWeight += weight;
  });
  
  return totalWeight > 0 ? (totalScore / totalWeight).toFixed(1) : '0.0';
};

const calculateComplaintRate = (data) => {
  if (!data) return 0;
  
  const complaints = data.filter(d => 
    (d.question_code?.startsWith('Q7[') || d.question_code?.startsWith('Q8[')) && d.answer === 'Да'
  );
  
  let totalComplaints = 0;
  let totalRespondents = 0;
  
  complaints.forEach(complaint => {
    totalComplaints += complaint.responses_count || 0;
    totalRespondents = Math.max(totalRespondents, complaint.total_respondents || 0);
  });
  
  if (totalRespondents === 0) return 0;
  
  return Math.round((totalComplaints / totalRespondents) * 100);
};

const calculateQualityIndex = (surveyData) => {
  if (!surveyData?.children) return 0;
  
  const kidsSatisfactionData = surveyData.children.find(d => 
    d.question_code === 'Q6' && d.answer === 'да'
  );
  const satisfaction = kidsSatisfactionData ? 
    parseFloat(kidsSatisfactionData.percentage.toString().replace(',', '.')) : 0;
  
  const avgRating = parseFloat(calculateAverageRating(surveyData.children));
  const complaintRate = calculateComplaintRate(surveyData.children);
  
  const normalizedRating = Math.round((avgRating / 5) * 100);
  const satisfactionContribution = Math.round(satisfaction * 0.4);
  const ratingContribution = Math.round(normalizedRating * 0.4);
  const complaintContribution = Math.round((100 - complaintRate) * 0.2);
  
  return Math.round(satisfactionContribution + ratingContribution + complaintContribution);
};

const getShortLabel = (text, maxLength = 20) => {
  if (!text) return '';
  const parts = text.split(' - ');
  const label = parts[1] || parts[0];
  return label.length > maxLength ? label.substring(0, maxLength) + '...' : label;
};

const getCategoryLabel = (category) => {
  const labels = {
    breakfast: '🍳 Горячие блюда завтрака',
    soups: '🍲 Первые блюда (супы)',
    mainDishes: '🍖 Вторые блюда',
    sides: '🥔 Гарниры',
    fruits: '🍎 Фрукты',
    bakery: '🥐 Выпечка',
    dairy: '🥛 Молочные продукты',
    bread: '🍞 Хлеб',
    drinks: '🥤 Напитки'
  };
  return labels[category] || category;
};

const getQualityColor = (index) => {
  if (index >= 80) return '#2a9d8f';
  if (index >= 60) return '#e9c46a';
  return '#e76f51';
};

const getQualityText = (index) => {
  if (index >= 80) return 'Отличное качество';
  if (index >= 60) return 'Хорошее качество';
  if (index >= 40) return 'Удовлетворительное';
  return 'Требует улучшений';
};

const getQualityInterpretation = (index) => {
  if (index >= 80) return 'Высокие показатели удовлетворенности и оценок, минимальное количество жалоб';
  if (index >= 60) return 'Стабильные показатели, есть потенциал для улучшений';
  if (index >= 40) return 'Требуются точечные улучшения в проблемных зонах';
  return 'Значительные проблемы с качеством питания и удовлетворенностью';
};

const SurveyModal = ({ surveyData, entityName, level, onClose }) => {
  const chartRefs = useRef({});
  const modalContentRef = useRef();
  const modalBodyRef = useRef();

  // Цветовая гамма на основе #1b567b
  const colors = {
    primary: '#1b567b',
    secondary: '#2c7da0',
    accent: '#468faf',
    light: '#89c2d9',
    lightest: '#a9d6e5',
    success: '#2a9d8f',
    warning: '#e9c46a',
    error: '#e76f51',
    background: '#f8f9fa'
  };

  // Функция экспорта структурированного отчета
  const exportReport = async () => {
  try {
    const exportButton = document.querySelector('.export-report-btn');
    exportButton.innerHTML = '📝 Формирование отчета...';
    exportButton.disabled = true;

    // Простой вызов
    await downloadPdfReport(surveyData, entityName, level);

    exportButton.innerHTML = '📄 Экспорт отчета';
    exportButton.disabled = false;

  } catch (error) {
    console.error('Ошибка при экспорте отчета:', error);
    
    const exportButton = document.querySelector('.export-report-btn');
    if (exportButton) {
      exportButton.innerHTML = '📄 Экспорт отчета';
      exportButton.disabled = false;
    }
  }
};

  // Альтернативный метод экспорта через html2canvas
  const exportToPDFAlternative = async () => {
    if (!modalBodyRef.current) return;

    try {
      const exportButton = document.querySelector('.export-report-btn');
      const originalText = exportButton.innerHTML;
      exportButton.innerHTML = '🔄 Альтернативный экспорт...';

      const canvas = await html2canvas(modalBodyRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: modalBodyRef.current.scrollWidth,
        height: modalBodyRef.current.scrollHeight
      });

      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let position = 0;
      let currentPage = 0;

      while (position < imgHeight) {
        if (currentPage > 0) {
          pdf.addPage();
        }

        const pageHeight = imgHeight > pdfHeight ? pdfHeight : imgHeight;
        const sourceY = position * (canvas.height / imgHeight);
        const sourceHeight = pageHeight * (canvas.height / imgHeight);

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(
          canvas, 
          0, sourceY, canvas.width, sourceHeight,
          0, 0, canvas.width, sourceHeight
        );

        const pageImgData = pageCanvas.toDataURL('image/png');
        
        pdf.addImage(
          pageImgData, 
          'PNG', 
          10, 
          10, 
          imgWidth, 
          Math.min(pageHeight, pdfHeight - 20)
        );

        position += pageHeight;
        currentPage++;
      }

      pdf.save(`Analiticheskii_otchet_${entityName}_${new Date().toLocaleDateString('ru-RU')}.pdf`);

      exportButton.innerHTML = originalText;
      exportButton.disabled = false;

    } catch (error) {
      console.error('Ошибка при альтернативном экспорте:', error);
      throw error;
    }
  };

  // Функция экспорта в PDF с полным захватом контента
  const exportToPDF = async () => {
    if (!modalBodyRef.current) return;

    try {
      const exportButton = document.querySelector('.export-pdf-btn');
      const originalText = exportButton.innerHTML;
      exportButton.innerHTML = '🔄 Подготовка PDF...';
      exportButton.disabled = true;

      const originalOverflow = modalBodyRef.current.style.overflow;
      const originalHeight = modalBodyRef.current.style.height;
      const originalMaxHeight = modalBodyRef.current.style.maxHeight;
      
      modalBodyRef.current.style.overflow = 'visible';
      modalBodyRef.current.style.height = 'auto';
      modalBodyRef.current.style.maxHeight = 'none';

      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(modalBodyRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: modalBodyRef.current.scrollWidth,
        height: modalBodyRef.current.scrollHeight,
        windowWidth: modalBodyRef.current.scrollWidth,
        windowHeight: modalBodyRef.current.scrollHeight,
        onclone: (clonedDoc) => {
          const clonedBody = clonedDoc.querySelector('.modal-body.compact');
          if (clonedBody) {
            clonedBody.style.overflow = 'visible';
            clonedBody.style.height = 'auto';
            clonedBody.style.maxHeight = 'none';
          }
        }
      });

      modalBodyRef.current.style.overflow = originalOverflow;
      modalBodyRef.current.style.height = originalHeight;
      modalBodyRef.current.style.maxHeight = originalMaxHeight;

      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let position = 0;
      let currentPage = 0;

      while (position < imgHeight) {
        if (currentPage > 0) {
          pdf.addPage();
        }

        const pageHeight = imgHeight > pdfHeight ? pdfHeight : imgHeight;
        const sourceY = position * (canvas.height / imgHeight);
        const sourceHeight = pageHeight * (canvas.height / imgHeight);

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(
          canvas, 
          0, sourceY, canvas.width, sourceHeight,
          0, 0, canvas.width, sourceHeight
        );

        const pageImgData = pageCanvas.toDataURL('image/png');
        
        pdf.addImage(
          pageImgData, 
          'PNG', 
          10, 
          10, 
          imgWidth, 
          Math.min(pageHeight, pdfHeight - 20)
        );

        position += pageHeight;
        currentPage++;
      }

      pdf.save(`Опрос_питания_${entityName}_${new Date().toLocaleDateString('ru-RU')}.pdf`);

      exportButton.innerHTML = originalText;
      exportButton.disabled = false;

    } catch (error) {
      console.error('Ошибка при экспорте в PDF:', error);
      alert('Произошла ошибка при экспорте в PDF. Попробуйте еще раз.');
      
      const exportButton = document.querySelector('.export-pdf-btn');
      if (exportButton) {
        exportButton.innerHTML = '📊 Экспорт в PDF';
        exportButton.disabled = false;
      }
    }
  };

  // Эффект для создания/обновления графиков
  useEffect(() => {
    if (!surveyData) return;

    createCharts();

    return () => {
      Object.values(chartRefs.current).forEach(chart => {
        if (chart) chart.destroy();
      });
    };
  }, [surveyData]);

  const createCharts = () => {
    if (!surveyData?.children) return;

    createRatingsChart();
    createComfortChart();
    createPopularDishesByTypeChart();
    createUnpopularDishesByTypeChart();
    createBuffetProfileChart();
    createParentControlChart();
    createComparisonChart();
    createMealFrequencyChart();
    createComplaintsChart();
    createNutritionEducationChart();
  };

  // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ДЛЯ РАБОТЫ С БЛЮДАМИ
  const getDishesByCategory = useMemo(() => {
    if (!surveyData?.children) return { popular: {}, unpopular: {} };

    const popular = {
      breakfast: surveyData.children
        .filter(d => d.question_code?.startsWith('Q12[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      soups: surveyData.children
        .filter(d => d.question_code?.startsWith('Q13[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      mainDishes: surveyData.children
        .filter(d => d.question_code?.startsWith('Q14[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      sides: surveyData.children
        .filter(d => d.question_code?.startsWith('Q15[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      fruits: surveyData.children
        .filter(d => d.question_code?.startsWith('Q16[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      bakery: surveyData.children
        .filter(d => d.question_code?.startsWith('Q17[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      dairy: surveyData.children
        .filter(d => d.question_code?.startsWith('Q18[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      bread: surveyData.children
        .filter(d => d.question_code?.startsWith('Q19[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      drinks: surveyData.children
        .filter(d => d.question_code?.startsWith('Q20[') && d.answer === 'Да')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5)
    };

    const unpopular = {
      breakfast: surveyData.children
        .filter(d => d.question_code?.startsWith('Q12[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      soups: surveyData.children
        .filter(d => d.question_code?.startsWith('Q13[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      mainDishes: surveyData.children
        .filter(d => d.question_code?.startsWith('Q14[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      sides: surveyData.children
        .filter(d => d.question_code?.startsWith('Q15[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      fruits: surveyData.children
        .filter(d => d.question_code?.startsWith('Q16[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      bakery: surveyData.children
        .filter(d => d.question_code?.startsWith('Q17[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      dairy: surveyData.children
        .filter(d => d.question_code?.startsWith('Q18[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      bread: surveyData.children
        .filter(d => d.question_code?.startsWith('Q19[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5),
      
      drinks: surveyData.children
        .filter(d => d.question_code?.startsWith('Q20[') && d.answer === 'Нет')
        .sort((a, b) => parseFloat(b.percentage.toString().replace(',', '.')) - parseFloat(a.percentage.toString().replace(',', '.')))
        .slice(0, 5)
    };

    return { popular, unpopular };
  }, [surveyData]);

  // ВЫЧИСЛЕНИЯ ДЛЯ СТАТИСТИК
  const statistics = useMemo(() => {
    if (!surveyData || !surveyData.children || !surveyData.parents) {
      return null;
    }

    const childrenTotal = surveyData.children.find(d => d.question_code === 'Q1')?.total_respondents || 0;
    const parentsTotal = surveyData.parents.find(d => d.question_code === 'Q1')?.total_respondents || 0;
    
    const kidsSatisfactionData = surveyData.children.find(d => 
      d.question_code === 'Q6' && d.answer === 'да'
    );
    const kidsSatisfaction = kidsSatisfactionData ? 
      parseFloat(kidsSatisfactionData.percentage.toString().replace(',', '.')) : 0;
    
    const parentsSatisfactionData = surveyData.parents.find(d => 
      d.question_code === 'Q24' && d.answer === 'да, абсолютно доволен'
    );
    const parentsSatisfaction = parentsSatisfactionData ? 
      parseFloat(parentsSatisfactionData.percentage.toString().replace(',', '.')) : 0;
    
    const avgRating = calculateAverageRating(surveyData.children);
    const qualityIndex = calculateQualityIndex(surveyData);

    const satisfaction = kidsSatisfaction;
    const avgRatingNum = parseFloat(avgRating);
    const complaintRate = calculateComplaintRate(surveyData.children);
    
    const normalizedRating = Math.round((avgRatingNum / 5) * 100);
    const satisfactionContribution = Math.round(satisfaction * 0.4);
    const ratingContribution = Math.round(normalizedRating * 0.4);
    const complaintContribution = Math.round((100 - complaintRate) * 0.2);

    return {
      childrenTotal,
      parentsTotal,
      kidsSatisfaction,
      parentsSatisfaction,
      avgRating,
      qualityIndex,
      calculationDetails: {
        satisfaction,
        satisfactionContribution,
        avgRating: avgRatingNum,
        normalizedRating,
        ratingContribution,
        complaintRate,
        complaintContribution
      }
    };
  }, [surveyData]);

  // ВЫЧИСЛЕНИЯ ДЛЯ РОДИТЕЛЬСКОГО КОНТРОЛЯ
  const parentControlStats = useMemo(() => {
    if (!surveyData?.parents) return null;

    const knowAboutControl = surveyData.parents.find(d => 
      d.question_code === 'Q30' && d.answer === 'знаю'
    );
    
    const effectiveControl = surveyData.parents.find(d => 
      d.question_code === 'Q35' && d.answer === 'да, это эффективная структура, благодаря которой питание детей улучшается'
    );

    const committeeExists = surveyData.parents.find(d => 
      d.question_code === 'Q34' && d.answer === 'да'
    );

    const willingToParticipate = surveyData.parents.find(d => 
      d.question_code === 'Q36' && (d.answer === 'да' || d.answer === 'да, при условии предоставления обучающих курсов для родителей' || d.answer === 'да, при условии конструктивного диалога с операторами питания или руководством пищеблока школы')
    );

    return {
      knowAboutControl: knowAboutControl ? parseFloat(knowAboutControl.percentage.toString().replace(',', '.')) : 0,
      effectiveControl: effectiveControl ? parseFloat(effectiveControl.percentage.toString().replace(',', '.')) : 0,
      committeeExists: committeeExists ? parseFloat(committeeExists.percentage.toString().replace(',', '.')) : 0,
      willingToParticipate: willingToParticipate ? parseFloat(willingToParticipate.percentage.toString().replace(',', '.')) : 0
    };
  }, [surveyData]);

  // ВЫЧИСЛЕНИЯ ДЛЯ ОБУЧЕНИЯ ПИТАНИЮ
  const nutritionEducationStats = useMemo(() => {
    if (!surveyData?.children) return null;

    const schoolEducation = surveyData.children.find(d => 
      d.question_code === 'Q27' && d.answer === 'да'
    );

    const homeEducationAlways = surveyData.children.find(d => 
      d.question_code === 'Q28' && d.answer === 'да'
    );
    
    const homeEducationSometimes = surveyData.children.find(d => 
      d.question_code === 'Q28' && d.answer === 'иногда'
    );

    const parentAwareness = surveyData.parents?.find(d => 
      d.question_code === 'Q41[5]' && d.answer === 'Да'
    );

    return {
      schoolEducation: schoolEducation ? parseFloat(schoolEducation.percentage.toString().replace(',', '.')) : 0,
      homeEducationAlways: homeEducationAlways ? parseFloat(homeEducationAlways.percentage.toString().replace(',', '.')) : 0,
      homeEducationSometimes: homeEducationSometimes ? parseFloat(homeEducationSometimes.percentage.toString().replace(',', '.')) : 0,
      parentAwareness: parentAwareness ? parseFloat(parentAwareness.percentage.toString().replace(',', '.')) : 0
    };
  }, [surveyData]);

  // Остальные функции компонента (createRatingsChart, createComfortChart и т.д.) остаются без изменений
  // ... [вставьте здесь все остальные функции компонента из предыдущего кода]

    const createRatingsChart = () => {
    const ctx = document.getElementById('ratingsChart')?.getContext('2d');
    if (!ctx) return;

    const breakfastRatings = surveyData.children.filter(d => 
      d.question_code === 'Q10' && d.answer?.includes('балл')
    ).sort((a, b) => parseInt(a.answer) - parseInt(b.answer));

    const lunchRatings = surveyData.children.filter(d => 
      d.question_code === 'Q11' && d.answer?.includes('балл')
    ).sort((a, b) => parseInt(a.answer) - parseInt(b.answer));

    if (chartRefs.current.ratingsChart) {
      chartRefs.current.ratingsChart.destroy();
    }

    chartRefs.current.ratingsChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: breakfastRatings.map(r => r.answer),
        datasets: [
          {
            label: 'Завтраки',
            data: breakfastRatings.map(r => parseFloat(r.percentage.toString().replace(',', '.'))),
            backgroundColor: colors.primary,
            borderRadius: 4
          },
          {
            label: 'Обеды',
            data: lunchRatings.map(r => parseFloat(r.percentage.toString().replace(',', '.'))),
            backgroundColor: colors.accent,
            borderRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              boxWidth: 12,
              padding: 8
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  };
 
  const createComfortChart = () => {
    const ctx = document.getElementById('comfortChart')?.getContext('2d');
    if (!ctx) return;

    const satisfiedData = surveyData.children.find(d => 
      d.question_code === 'Q9' && d.answer === 'да'
    );
    const sometimesData = surveyData.children.find(d => 
      d.question_code === 'Q9' && d.answer === 'не знаю'
    );

    const satisfied = satisfiedData ? parseFloat(satisfiedData.percentage.toString().replace(',', '.')) : 0;
    const sometimes = sometimesData ? parseFloat(sometimesData.percentage.toString().replace(',', '.')) : 0;
    const notSatisfied = Math.max(0, 100 - satisfied - sometimes);

    if (chartRefs.current.comfortChart) {
      chartRefs.current.comfortChart.destroy();
    }

    chartRefs.current.comfortChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Сыт после еды', 'Не уверен', 'Не сыт'],
        datasets: [{
          data: [satisfied, sometimes, notSatisfied],
          backgroundColor: [colors.success, colors.warning, colors.error],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 8,
              padding: 10
            }
          }
        }
      }
    });
  };

  // НОВЫЙ ГРАФИК: Популярные блюда по категориям
  const createPopularDishesByTypeChart = () => {
    const ctx = document.getElementById('popularDishesByTypeChart')?.getContext('2d');
    if (!ctx) return;

    const categories = [
      { key: 'breakfast', label: 'Завтраки', color: colors.primary },
      { key: 'soups', label: 'Супы', color: colors.secondary },
      { key: 'mainDishes', label: 'Основные блюда', color: colors.accent },
      { key: 'sides', label: 'Гарниры', color: colors.light },
      { key: 'fruits', label: 'Фрукты', color: colors.success },
      { key: 'bakery', label: 'Выпечка', color: colors.warning },
      { key: 'dairy', label: 'Молочные продукты', color: colors.lightest },
      { key: 'bread', label: 'Хлеб', color: '#d4a574' },
      { key: 'drinks', label: 'Напитки', color: '#a8dadc' }
    ];

    const datasets = categories.map(category => {
      const dishes = getDishesByCategory.popular[category.key];
      const topDish = dishes && dishes.length > 0 ? dishes[0] : null;
      
      return {
        label: category.label,
        data: [topDish ? parseFloat(topDish.percentage.toString().replace(',', '.')) : 0],
        backgroundColor: category.color,
        borderRadius: 4
      };
    }).filter(dataset => dataset.data[0] > 0);

    if (chartRefs.current.popularDishesByTypeChart) {
      chartRefs.current.popularDishesByTypeChart.destroy();
    }

    if (datasets.length === 0) {
      const noDataCtx = document.getElementById('popularDishesByTypeChart')?.getContext('2d');
      if (noDataCtx) {
        noDataCtx.fillStyle = '#f8f9fa';
        noDataCtx.fillRect(0, 0, noDataCtx.canvas.width, noDataCtx.canvas.height);
        noDataCtx.fillStyle = '#666';
        noDataCtx.textAlign = 'center';
        noDataCtx.fillText('Нет данных о популярных блюдах', noDataCtx.canvas.width / 2, noDataCtx.canvas.height / 2);
      }
      return;
    }

    chartRefs.current.popularDishesByTypeChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Топ-1 по категориям'],
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              boxWidth: 12,
              padding: 8,
              font: {
                size: 10
              }
            }
          },
          tooltip: {
            callbacks: {
              afterLabel: function(context) {
                const category = context.dataset.label;
                const dishes = getDishesByCategory.popular[
                  category === 'Завтраки' ? 'breakfast' :
                  category === 'Супы' ? 'soups' :
                  category === 'Основные блюда' ? 'mainDishes' :
                  category === 'Гарниры' ? 'sides' :
                  category === 'Фрукты' ? 'fruits' :
                  category === 'Выпечка' ? 'bakery' :
                  category === 'Молочные продукты' ? 'dairy' :
                  category === 'Хлеб' ? 'bread' : 'drinks'
                ];
                
                if (dishes && dishes.length > 0) {
                  const topDish = dishes[0];
                  return getShortLabel(topDish.question_text, 40);
                }
                return '';
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  };

  // НОВЫЙ ГРАФИК: Непопулярные блюда по категориям
  const createUnpopularDishesByTypeChart = () => {
    const ctx = document.getElementById('unpopularDishesByTypeChart')?.getContext('2d');
    if (!ctx) return;

    const categories = [
      { key: 'breakfast', label: 'Завтраки', color: colors.primary },
      { key: 'soups', label: 'Супы', color: colors.secondary },
      { key: 'mainDishes', label: 'Основные блюда', color: colors.accent },
      { key: 'sides', label: 'Гарниры', color: colors.light },
      { key: 'fruits', label: 'Фрукты', color: colors.success },
      { key: 'bakery', label: 'Выпечка', color: colors.warning },
      { key: 'dairy', label: 'Молочные продукты', color: colors.lightest },
      { key: 'bread', label: 'Хлеб', color: '#d4a574' },
      { key: 'drinks', label: 'Напитки', color: '#a8dadc' }
    ];

    const datasets = categories.map(category => {
      const dishes = getDishesByCategory.unpopular[category.key];
      const topDish = dishes && dishes.length > 0 ? dishes[0] : null;
      
      return {
        label: category.label,
        data: [topDish ? parseFloat(topDish.percentage.toString().replace(',', '.')) : 0],
        backgroundColor: category.color,
        borderRadius: 4
      };
    }).filter(dataset => dataset.data[0] > 0);

    if (chartRefs.current.unpopularDishesByTypeChart) {
      chartRefs.current.unpopularDishesByTypeChart.destroy();
    }

    if (datasets.length === 0) {
      const noDataCtx = document.getElementById('unpopularDishesByTypeChart')?.getContext('2d');
      if (noDataCtx) {
        noDataCtx.fillStyle = '#f8f9fa';
        noDataCtx.fillRect(0, 0, noDataCtx.canvas.width, noDataCtx.canvas.height);
        noDataCtx.fillStyle = '#666';
        noDataCtx.textAlign = 'center';
        noDataCtx.fillText('Нет данных о непопулярных блюдах', noDataCtx.canvas.width / 2, noDataCtx.canvas.height / 2);
      }
      return;
    }

    chartRefs.current.unpopularDishesByTypeChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Топ-1 по категориям'],
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              boxWidth: 12,
              padding: 8,
              font: {
                size: 10
              }
            }
          },
          tooltip: {
            callbacks: {
              afterLabel: function(context) {
                const category = context.dataset.label;
                const dishes = getDishesByCategory.unpopular[
                  category === 'Завтраки' ? 'breakfast' :
                  category === 'Супы' ? 'soups' :
                  category === 'Основные блюда' ? 'mainDishes' :
                  category === 'Гарниры' ? 'sides' :
                  category === 'Фрукты' ? 'fruits' :
                  category === 'Выпечка' ? 'bakery' :
                  category === 'Молочные продукты' ? 'dairy' :
                  category === 'Хлеб' ? 'bread' : 'drinks'
                ];
                
                if (dishes && dishes.length > 0) {
                  const topDish = dishes[0];
                  return getShortLabel(topDish.question_text, 40);
                }
                return '';
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  };

  const createBuffetProfileChart = () => {
    const ctx = document.getElementById('buffetProfileChart')?.getContext('2d');
    if (!ctx) return;

    const buyersData = surveyData.children.find(d => 
      d.question_code === 'Q25' && d.answer === 'да'
    );
    const sometimesData = surveyData.children.find(d => 
      d.question_code === 'Q25' && d.answer === 'иногда'
    );

    const buyers = buyersData ? parseFloat(buyersData.percentage.toString().replace(',', '.')) : 0;
    const sometimes = sometimesData ? parseFloat(sometimesData.percentage.toString().replace(',', '.')) : 0;
    const notBuyers = Math.max(0, 100 - buyers - sometimes);

    if (chartRefs.current.buffetProfileChart) {
      chartRefs.current.buffetProfileChart.destroy();
    }

    chartRefs.current.buffetProfileChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: ['Регулярно', 'Иногда', 'Не покупают'],
        datasets: [{
          data: [buyers, sometimes, notBuyers],
          backgroundColor: [colors.primary, colors.accent, colors.light],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 8,
              padding: 8
            }
          }
        }
      }
    });
  };

  const createParentControlChart = () => {
    const ctx = document.getElementById('parentControlChart')?.getContext('2d');
    if (!ctx || !surveyData.parents) return;

    const controlData = surveyData.parents.filter(d => d.question_code === 'Q31');
    
    if (chartRefs.current.parentControlChart) {
      chartRefs.current.parentControlChart.destroy();
    }

    chartRefs.current.parentControlChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: controlData.map(d => getShortLabel(d.answer, 15)),
        datasets: [{
          label: 'Родители',
          data: controlData.map(d => parseFloat(d.percentage.toString().replace(',', '.'))),
          backgroundColor: colors.primary,
          borderRadius: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  };

  const createComparisonChart = () => {
    const ctx = document.getElementById('comparisonChart')?.getContext('2d');
    if (!ctx) return;

    const kidsSatisfactionData = surveyData.children.find(d => 
      d.question_code === 'Q6' && d.answer === 'да'
    );
    
    const parentsSatisfactionData = surveyData.parents ? 
      surveyData.parents.find(d => d.question_code === 'Q24' && d.answer === 'да, абсолютно доволен') : null;

    const kidsSatisfaction = kidsSatisfactionData ? 
      parseFloat(kidsSatisfactionData.percentage.toString().replace(',', '.')) : 0;
    const parentsSatisfaction = parentsSatisfactionData ? 
      parseFloat(parentsSatisfactionData.percentage.toString().replace(',', '.')) : 0;

    if (chartRefs.current.comparisonChart) {
      chartRefs.current.comparisonChart.destroy();
    }

    chartRefs.current.comparisonChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Дети', 'Родители'],
        datasets: [{
          label: 'Удовлетворенность (%)',
          data: [kidsSatisfaction, parentsSatisfaction],
          backgroundColor: [colors.primary, colors.accent]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  };

  const createMealFrequencyChart = () => {
    const ctx = document.getElementById('mealFrequencyChart')?.getContext('2d');
    if (!ctx) return;

    const mealData = surveyData.children.filter(d => 
      d.question_code === 'Q5'
    );

    if (chartRefs.current.mealFrequencyChart) {
      chartRefs.current.mealFrequencyChart.destroy();
    }

    chartRefs.current.mealFrequencyChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: mealData.map(d => getShortLabel(d.answer, 20)),
        datasets: [{
          data: mealData.map(d => parseFloat(d.percentage.toString().replace(',', '.'))),
          backgroundColor: [colors.primary, colors.secondary, colors.accent],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 8,
              padding: 8
            }
          }
        }
      }
    });
  };

  const createComplaintsChart = () => {
    const ctx = document.getElementById('complaintsChart')?.getContext('2d');
    if (!ctx) return;

    const complaintsData = surveyData.parents?.filter(d => 
      d.question_code === 'Q25'
    );

    if (!complaintsData || complaintsData.length === 0) {
      console.log('Нет данных о жалобах');
      return;
    }

    if (chartRefs.current.complaintsChart) {
      chartRefs.current.complaintsChart.destroy();
    }

    chartRefs.current.complaintsChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: complaintsData.map(d => getShortLabel(d.answer, 12)),
        datasets: [{
          label: 'Частота жалоб (%)',
          data: complaintsData.map(d => parseFloat(d.percentage.toString().replace(',', '.'))),
          backgroundColor: colors.warning,
          borderRadius: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  };

  const createNutritionEducationChart = () => {
    const ctx = document.getElementById('nutritionEducationChart')?.getContext('2d');
    if (!ctx || !nutritionEducationStats) return;

    if (chartRefs.current.nutritionEducationChart) {
      chartRefs.current.nutritionEducationChart.destroy();
    }

    chartRefs.current.nutritionEducationChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: ['Рассказывают в школе', 'Рассказывают дома', 'Иногда дома'],
        datasets: [{
          data: [
            nutritionEducationStats.schoolEducation,
            nutritionEducationStats.homeEducationAlways,
            nutritionEducationStats.homeEducationSometimes
          ],
          backgroundColor: [colors.primary, colors.success, colors.light],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 8,
              padding: 8
            }
          }
        }
      }
    });
  };


  // КОМПОНЕНТЫ ДЛЯ ТАБЛИЦ БЛЮД
  const PopularDishesTable = ({ category, dishes }) => {
    if (!dishes || dishes.length === 0) return null;

    return (
      <div className="dishes-table">
        <h5>{getCategoryLabel(category)}</h5>
        <div className="dishes-list">
          {dishes.map((dish, index) => (
            <div key={index} className="dish-item">
              <span className="dish-name">{getShortLabel(dish.question_text, 30)}</span>
              <span className="dish-percentage success">{parseFloat(dish.percentage.toString().replace(',', '.')).toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const UnpopularDishesTable = ({ category, dishes }) => {
    if (!dishes || dishes.length === 0) return null;

    return (
      <div className="dishes-table">
        <h5>{getCategoryLabel(category)}</h5>
        <div className="dishes-list">
          {dishes.map((dish, index) => (
            <div key={index} className="dish-item">
              <span className="dish-name">{getShortLabel(dish.question_text, 30)}</span>
              <span className="dish-percentage error">{parseFloat(dish.percentage.toString().replace(',', '.')).toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!surveyData) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content survey-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>📊 Результаты опроса - {entityName}</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="no-data">Нет данных опросов для {entityName}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content survey-modal" onClick={e => e.stopPropagation()} ref={modalContentRef}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2>📊 Результаты опроса - {entityName}</h2>
            <div className="modal-actions">
              <button 
                className="export-report-btn"
                onClick={exportReport}
                title="Экспорт структурированного отчета"
              >
                📄 Экспорт отчета
              </button>
              <button 
                className="export-pdf-btn"
                onClick={exportToPDF}
                title="Экспорт всего отчета в PDF"
              >
                📊 Экспорт в PDF
              </button>
              <button className="modal-close" onClick={onClose}>×</button>
            </div>
          </div>
        </div>
        
        <div className="modal-body compact" ref={modalBodyRef}>
          {/* ОСНОВНЫЕ ПОКАЗАТЕЛИ С ВЫДЕЛЕННЫМ ИНДЕКСОМ КАЧЕСТВА */}
          <div className="analysis-section fade-in">
            <div className="section-header compact">
              <h3>📈 Основные показатели</h3>
            </div>
            
            <div className="kpi-grid compact">
              <div className="kpi-card compact" style={{borderLeftColor: colors.primary}}>
                <div className="kpi-value">{statistics.childrenTotal}</div>
                <div className="kpi-label">Ответов детей</div>
              </div>
              <div className="kpi-card compact" style={{borderLeftColor: colors.secondary}}>
                <div className="kpi-value">{statistics.parentsTotal}</div>
                <div className="kpi-label">Ответов родителей</div>
              </div>
              <div className="kpi-card compact" style={{borderLeftColor: colors.success}}>
                <div className="kpi-value">{statistics.kidsSatisfaction}%</div>
                <div className="kpi-label">Довольны дети</div>
              </div>
              <div className="kpi-card compact" style={{borderLeftColor: colors.accent}}>
                <div className="kpi-value">{statistics.parentsSatisfaction}%</div>
                <div className="kpi-label">Довольны родители</div>
              </div>
              <div className="kpi-card compact" style={{borderLeftColor: colors.warning}}>
                <div className="kpi-value">{statistics.avgRating}</div>
                <div className="kpi-label">Средняя оценка</div>
              </div>
              <div className="kpi-card compact featured" style={{borderLeftColor: getQualityColor(statistics.qualityIndex)}}>
                <div className="kpi-value large">{statistics.qualityIndex}</div>
                <div className="kpi-label">Индекс качества</div>
              </div>
            </div>

            {/* ДЕТАЛЬНОЕ ОПИСАНИЕ ИНДЕКСА КАЧЕСТВА */}
            {statistics.calculationDetails && (
              <div className="quality-details-card" style={{borderColor: getQualityColor(statistics.qualityIndex)}}>
                <div className="quality-header">
                  <h4>📊 Расчет индекса качества питания</h4>
                  <div className="quality-badge" style={{backgroundColor: getQualityColor(statistics.qualityIndex)}}>
                    {getQualityText(statistics.qualityIndex)}
                  </div>
                </div>
                <div className="calculation-steps">
                  <div className="calculation-step">
                    <span className="step-title">Удовлетворенность детей ({statistics.calculationDetails.satisfaction}%)</span>
                    <span className="step-calculation">× 40% = {statistics.calculationDetails.satisfactionContribution} баллов</span>
                  </div>
                  <div className="calculation-step">
                    <span className="step-title">Средняя оценка ({statistics.calculationDetails.avgRating}/5 → {statistics.calculationDetails.normalizedRating}%)</span>
                    <span className="step-calculation">× 40% = {statistics.calculationDetails.ratingContribution} баллов</span>
                  </div>
                  <div className="calculation-step">
                    <span className="step-title">Отсутствие жалоб ({100 - statistics.calculationDetails.complaintRate}%)</span>
                    <span className="step-calculation">× 20% = {statistics.calculationDetails.complaintContribution} баллов</span>
                  </div>
                  <div className="calculation-total">
                    <span className="total-label">Итого:</span>
                    <span className="total-value">{statistics.qualityIndex} баллов</span>
                  </div>
                </div>
                <div className="quality-interpretation">
                  <strong>Интерпретация:</strong> {getQualityInterpretation(statistics.qualityIndex)}
                </div>
              </div>
            )}

            <div className="charts-grid compact">
              <div className="chart-card compact">
                <div className="chart-header">
                  <h4>Оценки завтраков и обедов</h4>
                </div>
                <div className="chart-container compact">
                  <canvas id="ratingsChart"></canvas>
                </div>
              </div>

              <div className="chart-card compact">
                <div className="chart-header">
                  <h4>Уровень сытости</h4>
                </div>
                <div className="chart-container compact">
                  <canvas id="comfortChart"></canvas>
                </div>
              </div>

              <div className="chart-card compact">
                <div className="chart-header">
                  <h4>Сравнение восприятия</h4>
                </div>
                <div className="chart-container compact">
                  <canvas id="comparisonChart"></canvas>
                </div>
              </div>
            </div>
          </div>

          {/* ПОПУЛЯРНЫЕ И НЕПОПУЛЯРНЫЕ БЛЮДА ПО КАТЕГОРИЯМ */}
          <div className="analysis-section fade-in">
            <div className="section-header compact">
              <h3>🍽️ Анализ блюд по категориям</h3>
            </div>

            <div className="charts-grid compact">
              <div className="chart-card compact">
                <div className="chart-header">
                  <h4>Самые популярные блюда</h4>
                  <small>Топ-1 по каждой категории</small>
                </div>
                <div className="chart-container compact">
                  <canvas id="popularDishesByTypeChart"></canvas>
                </div>
              </div>

              <div className="chart-card compact">
                <div className="chart-header">
                  <h4>Самые непопулярные блюда</h4>
                  <small>Топ-1 по каждой категории</small>
                </div>
                <div className="chart-container compact">
                  <canvas id="unpopularDishesByTypeChart"></canvas>
                </div>
              </div>
            </div>

            {/* ДЕТАЛЬНЫЕ ТАБЛИЦЫ */}
            <div className="dishes-details-grid">
              <div className="dishes-category">
                <h4>🎯 Популярные блюда</h4>
                <div className="dishes-tables">
                  <PopularDishesTable category="breakfast" dishes={getDishesByCategory.popular.breakfast} />
                  <PopularDishesTable category="soups" dishes={getDishesByCategory.popular.soups} />
                  <PopularDishesTable category="mainDishes" dishes={getDishesByCategory.popular.mainDishes} />
                  <PopularDishesTable category="sides" dishes={getDishesByCategory.popular.sides} />
                  <PopularDishesTable category="fruits" dishes={getDishesByCategory.popular.fruits} />
                  <PopularDishesTable category="bakery" dishes={getDishesByCategory.popular.bakery} />
                  <PopularDishesTable category="dairy" dishes={getDishesByCategory.popular.dairy} />
                  <PopularDishesTable category="bread" dishes={getDishesByCategory.popular.bread} />
                  <PopularDishesTable category="drinks" dishes={getDishesByCategory.popular.drinks} />
                </div>
              </div>

              <div className="dishes-category">
                <h4>🚫 Непопулярные блюда</h4>
                <div className="dishes-tables">
                  <UnpopularDishesTable category="breakfast" dishes={getDishesByCategory.unpopular.breakfast} />
                  <UnpopularDishesTable category="soups" dishes={getDishesByCategory.unpopular.soups} />
                  <UnpopularDishesTable category="mainDishes" dishes={getDishesByCategory.unpopular.mainDishes} />
                  <UnpopularDishesTable category="sides" dishes={getDishesByCategory.unpopular.sides} />
                  <UnpopularDishesTable category="fruits" dishes={getDishesByCategory.unpopular.fruits} />
                  <UnpopularDishesTable category="bakery" dishes={getDishesByCategory.unpopular.bakery} />
                  <UnpopularDishesTable category="dairy" dishes={getDishesByCategory.unpopular.dairy} />
                  <UnpopularDishesTable category="bread" dishes={getDishesByCategory.unpopular.bread} />
                  <UnpopularDishesTable category="drinks" dishes={getDishesByCategory.unpopular.drinks} />
                </div>
              </div>
            </div>
          </div>

          {/* БУФЕТ И ДОПОЛНИТЕЛЬНОЕ ПИТАНИЕ */}
          <div className="analysis-section fade-in">
            <div className="section-header compact">
              <h3>🛒 Буфет и дополнительное питание</h3>
            </div>

            <div className="charts-grid compact">
              <div className="chart-card compact">
                <div className="chart-header">
                  <h4>Покупатели буфета</h4>
                </div>
                <div className="chart-container compact">
                  <canvas id="buffetProfileChart"></canvas>
                </div>
              </div>

              <div className="chart-card compact">
                <div className="chart-header">
                  <h4>Частота жалоб</h4>
                </div>
                <div className="chart-container compact">
                  <canvas id="complaintsChart"></canvas>
                </div>
              </div>

              <div className="chart-card compact">
                <div className="chart-header">
                  <h4>Обучение питанию</h4>
                </div>
                <div className="chart-container compact">
                  <canvas id="nutritionEducationChart"></canvas>
                </div>
              </div>
            </div>
          </div>

          {/* РОДИТЕЛЬСКИЙ КОНТРОЛЬ */}
          {surveyData.parents && parentControlStats && (
            <div className="analysis-section fade-in">
              <div className="section-header compact">
                <h3>👨‍👩‍👧‍👦 Родительский контроль</h3>
              </div>

              <div className="charts-grid compact">
                <div className="chart-card compact">
                  <div className="chart-header">
                    <h4>Проверка питания</h4>
                  </div>
                  <div className="chart-container compact">
                    <canvas id="parentControlChart"></canvas>
                  </div>
                </div>
                
                <div className="info-card compact" style={{borderColor: colors.light}}>
                  <div className="info-content">
                    <h4>Эффективность контроля</h4>
                    <div className="stat-row">
                      <span className="stat-label">Знают о возможности контроля:</span>
                      <span className="stat-value">{parentControlStats.knowAboutControl}%</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Оценивают комиссию как эффективную:</span>
                      <span className="stat-value">{parentControlStats.effectiveControl}%</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Комиссия работает в школе:</span>
                      <span className="stat-value">{parentControlStats.committeeExists}%</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Готовы участвовать:</span>
                      <span className="stat-value">{parentControlStats.willingToParticipate}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyModal;